
"use client";

import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Loader2, TrendingUp, BarChart, History } from "lucide-react";
import SafetyZoneIndicator from "@/components/progress/SafetyZoneIndicator";
import SubjectBalanceChart from "@/components/progress/SubjectBalanceChart";
import PerformanceEvolutionChart from "@/components/progress/PerformanceEvolutionChart";
import TopicPerformanceChart from "@/components/progress/TopicPerformanceChart";
import { CoverageChart } from "@/components/progress/CoverageChart";
import ExamHistoryList from "@/components/progress/ExamHistoryList";
import { UserStats, ExamResultMetadata } from "@/lib/types";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import ExamResultSummary from "@/components/exam/ExamResultSummary";
import { ExamResult } from "@/lib/types";
import HelpTooltip from "@/components/ui/HelpTooltip";

export default function ProgressPage() {
    const [stats, setStats] = useState<UserStats | null>(null);
    const [history, setHistory] = useState<ExamResultMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedExamId, setSelectedExamId] = useState<string | null>(null);
    const [selectedExamDetails, setSelectedExamDetails] = useState<ExamResult | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, historyRes] = await Promise.all([
                    fetch('/api/exams/stats'),
                    fetch('/api/exams/history')
                ]);

                if (statsRes.ok && historyRes.ok) {
                    const statsData = await statsRes.json();
                    const historyData = await historyRes.json();
                    setStats(statsData);
                    setHistory(historyData);
                }
            } catch (error) {
                console.error("Failed to fetch progress data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSelectExam = async (examId: string) => {
        setSelectedExamId(examId);
        setLoadingDetails(true);
        try {
            const res = await fetch(`/api/exams/${examId}`);
            if (res.ok) {
                const data = await res.json();
                setSelectedExamDetails(data);
            } else {
                console.error("Failed to load exam details");
            }
        } catch (error) {
            console.error("Error loading exam details:", error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCloseModal = () => {
        setSelectedExamId(null);
        setSelectedExamDetails(null);
    };

    if (loading) {
        return (
            <div className="w-full h-[calc(100vh-100px)] flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Loading your progress...</p>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="w-full max-w-6xl mx-auto p-6">
                <div className="text-center py-12">
                    <h2 className="text-2xl font-bold">Unable to load progress</h2>
                    <p className="text-muted-foreground">Please try refreshing the page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
            <header className="mb-6">
                <h1 className="text-3xl font-bold tracking-tight text-primary flex items-center">
                    <TrendingUp className="w-8 h-8 mr-3 text-accent" />
                    My Progress
                </h1>
                <p className="text-muted-foreground mt-1">
                    Track your preparation for the Comenius University entrance exam.
                </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Top Row: Key Metrics */}
                <SafetyZoneIndicator passRate={stats.last10PassRate} />

                <SubjectBalanceChart
                    biology={stats.biologyStats}
                    chemistry={stats.chemistryStats}
                />

                <div className="grid grid-rows-2 gap-6">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
                                Total Exams Taken
                                <HelpTooltip content="The total number of practice exams you have completed. Consistency is key!" />
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-4xl font-bold">{stats.totalExams}</div>
                            <p className="text-xs text-muted-foreground mt-1">Lifetime practice sessions</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
                                Average Score
                                <HelpTooltip content="Your overall average score across all practice exams. Aim to keep this above 55%." />
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className={`text-4xl font-bold ${stats.averageScore >= 55 ? 'text-green-600' : 'text-yellow-600'}`}>
                                {stats.averageScore.toFixed(1)}%
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Average across all exams</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Middle Row: Evolution Chart */}
                <CoverageChart stats={stats.coverage} />
                <TopicPerformanceChart topicStats={stats.topicStats} />
                <PerformanceEvolutionChart data={stats.evolution} />

                {/* Bottom Section: History */}
                <ExamHistoryList history={history} onSelectExam={handleSelectExam} />
            </div>

            <Dialog open={!!selectedExamId} onOpenChange={(open) => !open && handleCloseModal()}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    {loadingDetails ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                            <p className="text-muted-foreground">Loading exam details...</p>
                        </div>
                    ) : selectedExamDetails ? (
                        <ExamResultSummary
                            finalExamScore={selectedExamDetails.score.score}
                            finalExamMaxPossibleScore={selectedExamDetails.score.maxPossibleScore}
                            totalQuestions={selectedExamDetails.questionsCount}
                            timeTaken={selectedExamDetails.score.timeTaken || 0}
                            mode={selectedExamDetails.mode}
                            questionSummaryForDisplay={selectedExamDetails.questionDetails}
                            attemptedCount={selectedExamDetails.questionDetails.length} // Assuming all are attempted or using length
                            onClose={handleCloseModal}
                        />
                    ) : (
                        <div className="text-center py-10 text-red-500">
                            Failed to load exam details.
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
